import React from 'react'
import { SectionHeader } from '@/components/molecules/SectionHeader'
import { ProcessStep } from '@/components/molecules/ProcessStep'
import './ProcessesSection.css'

export interface ProcessesSectionProps {
  className?: string
  id?: string
}

export interface Process {
  stepNumber: string
  title: string
  description: string
  subItems: Array<{
    number: string
    title: string
    description: string
  }>
}

const processes: Process[] = [
  {
    stepNumber: 'STEP 1',
    title: 'Discover',
    description:
      'This is the foundational phase where ambiguity is reduced and the "Why" of the product is established. The goal is to validate assumptions before investing resources in high-fidelity work.',
    subItems: [
      {
        number: '01.',
        title: 'Business Goals',
        description:
          'This defines what success looks like for the organization, ensuring the product is not just desirable to users, but viable for the business.',
      },
      {
        number: '02.',
        title: 'User Requirements',
        description:
          'Identifying the specific pain point the user is facing. This often involves "The 5 Whys" to get to the root cause.',
      },
      {
        number: '03.',
        title: 'Market Research',
        description:
          'Researching what solutions already exist, where they fail, and identifying the "Blue Ocean" (untapped market space) opportunities.',
      },
      {
        number: '04.',
        title: 'Deliverables',
        description:
          'Deliverables: User Personas, User Journey Maps, and a Problem Statement.',
      },
    ],

  },
  {
    stepNumber: 'STEP 2',
    title: 'Design',
    description:
      'Once the problem is defined, this phase focuses on the "What" and "How" of the solution. It translates abstract requirements into tangible visuals and interactions.',
    subItems: [
      {
        number: '01.',
        title: 'Information Architecture',
        description:
          'Structuring how content and navigation are organized so users can find what they need intuitively.',
      },
      {
        number: '02.',
        title: 'Wireframes & Prototypes',
        description:
          'Low-Fidelity: Structural skeletons (wireframes) to test layout and flow. High-Fidelity: Interactive prototypes that look and behave like the final product for user testing.',
      },
      {
        number: '03.',
        title: 'Deliverables',
        description:
          'High-fidelity UI screens, interactive prototypes, and a component library.',
      },
    ],
  },
  {
    stepNumber: 'STEP 03',
    title: 'Documentation & KT',
    description:
      'This is the bridge between the creative vision and engineering execution. It is the phase most critical for preventing "scope creep" and ensuring the developed product matches the design.',
    subItems: [
      {
        number: '01.',
        title: 'Functional Specifications (PRD)',
        description:
          'Writing detailed requirements describing how features function, including logic, edge cases, and error states.',
      },
      {
        number: '02.',
        title: 'Design Handoff:',
        description:
          'preparing assets (icons, images) and redlining specs (spacing, typography, hex codes) for developers. Tools like Zeplin or Figma’s Dev Mode are heavily used here.',
      },
      {
        number: '03.',
        title: 'Knowledge Transfer (KT) Sessions:',
        description:
          'Walkthrough meetings where designers and product managers explain the flow to engineers. This is where technical feasibility is often challenged and negotiated.',
      },
      {
        number: '04.',
        title: 'Deliverables:',
        description:
          'Product Requirement Documents (PRD), Design Specs, and User Stories/Acceptance Criteria.',
      },
    ],
  },
  {
    stepNumber: 'STEP 04',
    title: 'Devlopment',
    description:
      'This is the execution phase where the product is built, tested, and prepared for deployment.',
    subItems: [
      {
        number: '01.',
        title: 'Implementation:',
        description:
          'Frontend: Building the client-side interface that users interact with. Backend: Setting up the server, database, and APIs that power the logic.',
      },
      {
        number: '02.',
        title: 'Quality Assurance (QA):',
        description:
          'Testing the build against the original documentation to squash bugs. This includes functional testing (does it work?) and visual QA (does it look right?).',
      },
      {
        number: '03.',
        title: 'Deployment:',
        description:
          'Releasing the code to a staging environment for final review, and then to production for the end users.',
      },
      {
        number: '04.',
        title: 'Deliverables:',
        description:
          'A functional, bug-free product build.',
      },
    ],
  },
]

export const ProcessesSection: React.FC<ProcessesSectionProps> = ({
  className = '',
  id,
}) => {
  return (
    <section id={id} className={`processes-section ${className}`}>
      <SectionHeader
        title="Processes we follow"
        description="We provide meaningful and actionable data-driven insights that represent the voice of a user"
      />

      <div className="processes-section-steps">
        {processes.map((process, index) => (
          <ProcessStep
            key={index}
            stepNumber={process.stepNumber}
            title={process.title}
            description={process.description}
            subItems={process.subItems}
          />
        ))}
      </div>
    </section>
  )
}
